/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { IHeroBannerSlideConfig } from '../definition-extensions/hero-banner-slide.ext.props.autogenerated';
import { SwiperSlide } from 'swiper/react';

export default ({ config }: { config: IHeroBannerSlideConfig }) => {
    const [isMobile, setIsMobile] = React.useState(false);

    React.useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 767);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const backgroundImageDesktop = config.backgroundImageDesktop;
    const backgroundImageMobile = config.backgroundImageMobile;

    const backgroundImage = isMobile && backgroundImageMobile ? backgroundImageMobile : backgroundImageDesktop;

    const slideStyle = backgroundImage
        ? {
              backgroundImage: `url(${backgroundImage.src || backgroundImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center center',
              backgroundRepeat: 'no-repeat'
          }
        : {};

    const colorMap: { [key: string]: string } = {
        copperOrange: '#DC4E0F',
        warmBeige: '#D6D2C4',
        darkBrown: '#4B1800',
        cream: '#F7EFDD',
        yellow: '#f9c22e',
        white: '#FFFFFF',
        black: '#000000'
    };

    const titleStyle = config.titleColor ? { color: colorMap[config.titleColor] || config.titleColor } : {};

    const buttonClassName = config.buttonStyle === 'primary' ? 'hero-banner-button-primary' : 'hero-banner-button-secondary';

    const labelColor = config.labelColor ? colorMap[config.labelColor] || config.labelColor : colorMap.yellow;
    const labelStyle = {
        color: labelColor,
        borderColor: labelColor,
        backgroundColor: 'transparent',
        border: `solid 1px ${labelColor}`
    };
    const slideTitle = config.title || config.label || 'Slide';
    const contentPosition = config?.contentPosition || 'left';
    const paginationTheme = config.paginationTheme || 'light';

    return (
        <SwiperSlide style={slideStyle} aria-label={slideTitle} data-pagination-theme={paginationTheme}>
            <div className='container slide-container'>
                <div className={`slide-content content-${contentPosition}`}>
                    {config.label && (
                        <div className='slide-label' style={labelStyle}>
                            {config.label}
                        </div>
                    )}
                    {config.title && (
                        <h1 className='slide-title' style={titleStyle}>
                            {config.title}
                        </h1>
                    )}
                    {config.subtitle && <div dangerouslySetInnerHTML={{ __html: config.subtitle as string }} className='slide-subtitle' />}
                    <div className='slide-buttons-container'>
                        {config.buttonUrl && config.buttonText && (
                            <a
                                href={config.buttonUrl?.destinationUrl || '#'}
                                className={`slide-button ${buttonClassName}`}
                                aria-label={`${config.buttonText} - ${slideTitle}`}
                            >
                                {config.buttonText}
                            </a>
                        )}
                        {config.secondaryButtonText && config.secondaryButtonUrl && (
                            <a
                                href={config.secondaryButtonUrl?.destinationUrl || '#'}
                                className='slide-button slide-button-secondary'
                                aria-label={`${config.secondaryButtonText} - ${slideTitle}`}
                            >
                                {config.secondaryButtonText}
                            </a>
                        )}
                    </div>
                    {config.blockText && (
                        <div dangerouslySetInnerHTML={{ __html: config.blockText as string }} className='slide-block-text' />
                    )}
                </div>
            </div>
        </SwiperSlide>
    );
};
